<template>
  <div class="bph">
    <div class="bph-head">
      <div>
        <h3 class="bph-title">Bulk price update history</h3>
        <p class="bph-subtitle">
          Every bulk price change, who ran it, what is still to come, and how to
          put it back.
        </p>
      </div>
      <button class="bph-btn bph-btn-ghost" @click="$router.push('/menu-set')">
        <i class="material-icons">arrow_back</i> Back to Menu Set
      </button>
    </div>

    <div class="bph-panel">
      <div class="bph-table-wrap">
        <table class="bph-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Change</th>
              <th>How long</th>
              <th class="bph-num">Applied to</th>
              <th>Reason</th>
              <th>Status</th>
              <th class="bph-actions-col">Action</th>
            </tr>
          </thead>
          <tbody v-if="batches.length">
            <tr v-for="row in batches" :key="row.id">
              <td class="bph-when">
                <template v-if="row.applied_at">{{ dateTime(row.applied_at) }}</template>
                <template v-else-if="row.start_date">
                  <span class="bph-who">Starts {{ dateTime(row.start_date) }}</span>
                  <!-- cancelled and failed runs are never going to land, so
                       "not applied yet" would be a promise the row cannot keep -->
                  <span class="bph-sub">
                    {{ row.status === 5 ? "not applied yet" : "never applied" }}
                  </span>
                </template>
                <template v-else>—</template>
              </td>
              <td>
                <span class="bph-who">{{ row.applied_by_name || "—" }}</span>
                <span class="bph-sub" v-if="row.applied_by_email">
                  {{ row.applied_by_email }}
                </span>
              </td>
              <td>
                <span class="bph-op" :class="opClass(row)">{{ row.operation_label }}</span>
                <span class="bph-sub">{{ targetLabel(row.targets) }}</span>
              </td>
              <td>
                <span class="bph-who">{{ row.change_type_name || "Permanent" }}</span>
                <span class="bph-sub bph-window" v-if="row.is_temporary">{{ windowLabel(row) }}</span>
              </td>
              <td>
                <span class="bph-who">{{ scopeLabel(row) }}</span>
                <span class="bph-sub" v-if="row.category_name">{{ row.category_name }}</span>
                <span class="bph-sub">
                  {{ row.total_items }} item(s) · {{ row.total_lines }} price(s)
                </span>
              </td>
              <td class="bph-reason">{{ row.note || "—" }}</td>
              <td>
                <span class="bph-badge" :class="statusClass(row.status)">
                  {{ row.status_name }}
                </span>
                <span class="bph-sub" v-if="row.cancelled_at">
                  by {{ row.cancelled_by_name || "—" }}<br />on {{ dateTime(row.cancelled_at) }}
                </span>
                <span class="bph-sub" v-else-if="row.reverted_by_name">
                  by {{ row.reverted_by_name }}<br />on {{ dateTime(row.reverted_at) }}
                </span>
                <span class="bph-sub" v-if="row.fail_reason">{{ row.fail_reason }}</span>
              </td>
              <td class="bph-actions-col">
                <button class="bph-btn bph-btn-ghost bph-btn-sm" @click="openDetail(row)">
                  View
                </button>
                <button
                  v-if="row.can_revert"
                  class="bph-btn bph-btn-undo bph-btn-sm"
                  @click="confirmRevert(row)"
                >
                  <i class="material-icons">undo</i> Undo
                </button>
                <button
                  v-if="row.can_cancel"
                  class="bph-btn bph-btn-cancel bph-btn-sm"
                  @click="confirmCancel(row)"
                >
                  <i class="material-icons">event_busy</i> Cancel
                </button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="8" class="bph-empty">
                <i class="material-icons">history</i>
                <span>No bulk price update has been run yet.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination
        v-if="pagination.last_page > 1"
        :pagination="pagination"
        :current_page="pagination.current_page"
        :last_page="pagination.last_page"
      />
    </div>

    <!-- detail -->
    <button
      style="display: none"
      type="button"
      class="btn-open-modal-bulk-detail"
      data-bs-toggle="modal"
      data-bs-target="#modalBulkPriceDetail"
    ></button>
    <div class="modal fade" id="modalBulkPriceDetail" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog bph-dialog">
        <div class="modal-content bph-modal">
          <div class="bph-modal-head">
            <div>
              <h5 class="bph-modal-title">
                {{ detailBatch.status === 5 ? "Prices to change" : "Prices changed" }}
              </h5>
              <p class="bph-modal-sub">
                <span v-if="detailBatch.applied_at">{{ dateTime(detailBatch.applied_at) }} · </span>
                {{ detailBatch.applied_by_name }}
                <span> · {{ scopeLabel(detailBatch) }}</span>
                <span v-if="detailBatch.category_name"> · {{ detailBatch.category_name }}</span>
                <span v-if="detailBatch.is_temporary"> · {{ windowLabel(detailBatch) }}</span>
                <span v-if="detailBatch.note"> · {{ detailBatch.note }}</span>
              </p>
            </div>
            <button
              type="button"
              class="bph-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="material-icons">close</i>
            </button>
          </div>

          <div class="bph-modal-body">
            <div class="bph-table-wrap is-inner">
              <table class="bph-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Price</th>
                    <th class="bph-num">Before</th>
                    <th class="bph-num">After</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in detailLines" :key="line.id">
                    <td>
                      <span class="bph-who">{{ line.item_name }}</span>
                      <span class="bph-sub">{{ line.item_no }}</span>
                    </td>
                    <td><span class="bph-tag">{{ line.price_label }}</span></td>
                    <td class="bph-num bph-old">{{ money(line.old_value) }}</td>
                    <td class="bph-num bph-new">{{ money(line.new_value) }}</td>
                    <td>
                      <span class="bph-badge" :class="revertClass(line.revert_status)">
                        {{ revertLabel(line.revert_status) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!detailLines.length">
                    <td colspan="5" class="bph-empty">No line</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              class="bph-note"
              v-if="detailPagination.total > detailLines.length"
            >
              <i class="material-icons">info_outline</i>
              Showing {{ detailLines.length }} of {{ detailPagination.total }} lines.
            </p>
          </div>

          <div class="bph-modal-foot">
            <button type="button" class="bph-btn bph-btn-ghost" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalConfirmDelete :message="confirmMessage" :process-yes="runPendingAction" />

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import "bootstrap/dist/js/bootstrap.bundle";
import "bootstrap/js/dist/modal";
import $ from "jquery";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalConfirmDelete from "@/components/ModalConfirmDelete.vue";
import Pagination from "@/components/Pagination.vue";
import ItemService from "../../services/product/item.service";

export default {
  name: "BulkPriceHistory",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    ModalConfirmDelete,
    Pagination,
  },
  data() {
    return {
      batches: [],
      pagination: {},
      page: 1,
      loaderActive: false,
      snackbar: null,
      message: "",
      detailBatch: {},
      detailLines: [],
      detailPagination: {},
      pendingBatch: null,
      pendingAction: null,
    };
  },
  computed: {
    confirmMessage() {
      if (!this.pendingBatch) {
        return "";
      }

      if (this.pendingAction === "cancel") {
        return (
          "Cancel this scheduled update? " +
          this.pendingBatch.total_lines +
          " price(s) booked for " +
          this.dateTime(this.pendingBatch.start_date) +
          " will not happen. Nothing has changed yet, so no price moves."
        );
      }

      return (
        "Undo this update? " +
        this.pendingBatch.total_lines +
        " price(s) will go back to what they were. Prices edited by hand since " +
        "then are left as they are."
      );
    },
  },
  mounted() {
    if (this.$route.query.page) {
      this.page = this.$route.query.page;
    }
    this.getList(this.page);
  },
  methods: {
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    targetLabel(targets) {
      if (!targets) {
        return "";
      }

      const parts = [];
      if (targets.base) {
        parts.push("Base price");
      }
      (targets.price_keys || []).forEach((key) => {
        parts.push("Price " + key.replace("price_", ""));
      });
      (targets.platforms || []).forEach((platform) => parts.push(platform));

      return parts.join(", ");
    },
    /** A raise and a cut should be tellable apart at a glance. */
    opClass(row) {
      const label = row.operation_label || "";

      if (label.indexOf("-") === 0) {
        return "is-down";
      }

      return label.indexOf("+") === 0 ? "is-up" : "";
    },
    statusClass(status) {
      return {
        1: "is-applied",
        2: "is-reverted",
        3: "is-partial",
        4: "is-failed",
        5: "is-scheduled",
        6: "is-reverted",
        7: "is-cancelled",
      }[status] || "";
    },
    /** '2026-08-13 13:35:00' -> '13/08/2026 13:35'. Seconds are always zero. */
    dateTime(value) {
      if (!value) {
        return "";
      }

      // Split rather than new Date(): Safari reads '2026-08-13 13:35:00' as
      // invalid, and the string is already in the merchant's own clock.
      const parts = String(value).split(" ");
      const date = (parts[0] || "").split("-");

      if (date.length !== 3) {
        return value;
      }

      return (
        date[2] + "/" + date[1] + "/" + date[0] +
        (parts[1] ? " " + parts[1].substring(0, 5) : "")
      );
    },
    /**
     * What the run was pointed at. A run built from ticked items carries no
     * menu set, so it says so instead of showing an empty cell.
     */
    scopeLabel(row) {
      if (row.menuset_name) {
        return row.menuset_name;
      }

      if (row.selected_items) {
        return row.selected_items + " selected item(s)";
      }

      return "—";
    },
    /** 'from 13/08/2026 13:35 to 19/08/2026 11:35' under the Temporary tag. */
    windowLabel(row) {
      const from = row.start_date ? this.dateTime(row.start_date) : "";
      const to = row.end_date ? this.dateTime(row.end_date) : "";

      if (from && to) {
        return from + " → " + to;
      }

      return to ? "until " + to : from;
    },
    revertLabel(status) {
      return {
        0: "Applied",
        1: "Undone",
        2: "Skipped — edited since",
        3: "Waiting to be applied",
        4: "Cancelled",
      }[status] || "";
    },
    revertClass(status) {
      return {
        0: "is-applied",
        1: "is-reverted",
        2: "is-partial",
        3: "is-scheduled",
        4: "is-cancelled",
      }[status] || "";
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 5000);
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    getList(page) {
      this.showLoader();
      ItemService.bulkHistory({ page: page }).then(
        (response) => {
          this.hideLoader();
          if (response.data.result.isSuccess) {
            this.batches = response.data.data.items;
            this.pagination = response.data.data;
            this.page = this.pagination.current_page;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.hideLoader();
          this.snackbar = "danger";
          this.message = error.toString();
          this.setTimeOutNotification();
        }
      );
    },
    openDetail(row) {
      this.showLoader();
      ItemService.bulkDetail({ id: row.id, limit: 200 }).then(
        (response) => {
          this.hideLoader();
          if (response.data.result.isSuccess) {
            this.detailBatch = response.data.data.batch;
            this.detailLines = response.data.data.lines.items;
            this.detailPagination = response.data.data.lines;
            $(".btn-open-modal-bulk-detail").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.hideLoader();
          this.snackbar = "danger";
          this.message = error.toString();
          this.setTimeOutNotification();
        }
      );
    },
    confirmRevert(row) {
      this.pendingBatch = row;
      this.pendingAction = "revert";
      $(".btn-confirm-modal-delete").click();
    },
    confirmCancel(row) {
      this.pendingBatch = row;
      this.pendingAction = "cancel";
      $(".btn-confirm-modal-delete").click();
    },
    runPendingAction() {
      if (this.pendingAction === "cancel") {
        this.doCancel();
        return;
      }

      this.doRevert();
    },
    doCancel() {
      if (!this.pendingBatch) {
        return;
      }

      const id = this.pendingBatch.id;
      $(".btn-close-modal").click();
      this.showLoader();

      ItemService.bulkCancel({ id: id }).then(
        (response) => {
          this.hideLoader();
          if (response.data.result.isSuccess) {
            this.snackbar = "success";
            this.message =
              "Scheduled update cancelled. " +
              response.data.data.cancelled_lines +
              " price change(s) will not happen.";
            this.setTimeOutNotification();
            this.getList(this.page);
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            // The row is stale - the scheduler moved this run on while the
            // screen was open. Reload so the buttons match reality.
            this.getList(this.page);
          }
          this.pendingBatch = null;
          this.pendingAction = null;
        },
        (error) => {
          this.hideLoader();
          this.snackbar = "danger";
          this.message = error.toString();
          this.setTimeOutNotification();
          this.pendingBatch = null;
          this.pendingAction = null;
        }
      );
    },
    doRevert() {
      if (!this.pendingBatch) {
        return;
      }

      const id = this.pendingBatch.id;
      $(".btn-close-modal").click();
      this.showLoader();

      ItemService.bulkRevert({ id: id }).then(
        (response) => {
          this.hideLoader();
          if (response.data.result.isSuccess) {
            const data = response.data.data;
            this.snackbar = "success";
            this.message =
              "Undone " +
              data.reverted_lines +
              " price(s)." +
              (data.conflict_lines
                ? " " +
                  data.conflict_lines +
                  " left alone because they were edited after this update."
                : "");
            this.setTimeOutNotification();
            this.getList(this.page);
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            // The row is stale - the scheduler moved this run on while the
            // screen was open. Reload so the buttons match reality.
            this.getList(this.page);
          }
          this.pendingBatch = null;
          this.pendingAction = null;
        },
        (error) => {
          this.hideLoader();
          this.snackbar = "danger";
          this.message = error.toString();
          this.setTimeOutNotification();
          this.pendingBatch = null;
          this.pendingAction = null;
        }
      );
    },
  },
};
</script>

<style scoped>
/* Same self-contained palette as the bulk price popup, so the two screens read
   as one feature instead of two. Nothing here reuses theme class names. */
.bph {
  --bph-ink: #1c2530;
  --bph-ink-soft: #5b6672;
  --bph-line: #dfe3e8;
  --bph-line-strong: #b9c0c8;
  --bph-bg-soft: #f6f8fa;
  --bph-brand: #1871bd;
  --bph-up: #1a7f4b;
  --bph-down: #b3261e;

  padding: 24px 28px 40px;
  color: var(--bph-ink);
  font-size: 14px;
}

.bph-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.bph-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}

.bph-subtitle {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: var(--bph-ink-soft);
}

.bph-panel {
  border: 1px solid var(--bph-line);
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
}

.bph-table-wrap {
  overflow-x: auto;
}

.bph-table-wrap.is-inner {
  border: 1px solid var(--bph-line);
  border-radius: 12px;
  max-height: 52vh;
  overflow-y: auto;
}

.bph-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  /* Below this the dates and the target list wrap into three lines each, so
     let the wrapper scroll instead. */
  min-width: 1080px;
}

.bph-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 12px 16px;
  background: var(--bph-bg-soft);
  border-bottom: 1px solid var(--bph-line);
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bph-ink-soft);
  text-align: left;
  white-space: nowrap;
}

.bph-table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid #eef1f4;
  vertical-align: top;
}

.bph-table tbody tr:last-child td {
  border-bottom: 0;
}

.bph-table tbody tr:hover {
  background: #fafbfc;
}

.bph-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.bph-when {
  white-space: nowrap;
  color: var(--bph-ink-soft);
}

/* The temporary window reads as one thing; wrapping it mid-date hurts. */
.bph-window {
  white-space: nowrap;
}

.bph-who {
  display: block;
  font-weight: 500;
}

.bph-sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--bph-ink-soft);
}

.bph-reason {
  max-width: 260px;
  color: var(--bph-ink-soft);
}

.bph-op {
  display: block;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.bph-op.is-up {
  color: var(--bph-up);
}

.bph-op.is-down {
  color: var(--bph-down);
}

.bph-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--bph-bg-soft);
  font-size: 12px;
  color: var(--bph-ink-soft);
  white-space: nowrap;
}

.bph-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.bph-badge.is-applied {
  background: #e6f4ec;
  color: #1a7f4b;
}

.bph-badge.is-reverted {
  background: #eef1f4;
  color: #5b6672;
}

.bph-badge.is-partial {
  background: #fdf1dd;
  color: #8a5a06;
}

.bph-badge.is-failed {
  background: #fdeceb;
  color: #b3261e;
}

/* M892 - a run that has not happened yet, and one that was called off. */
.bph-badge.is-scheduled {
  background: #e8f2fb;
  color: #1871bd;
}

.bph-badge.is-cancelled {
  background: #eef1f4;
  color: #5b6672;
}

.bph-old {
  color: var(--bph-ink-soft);
}

.bph-new {
  font-weight: 600;
}

.bph-actions-col {
  text-align: right;
  white-space: nowrap;
}

.bph-empty {
  padding: 48px 16px !important;
  text-align: center;
  color: var(--bph-ink-soft);
}

.bph-empty .material-icons {
  display: block;
  margin: 0 auto 8px;
  font-size: 30px;
  opacity: 0.5;
}

/* ---------- buttons ---------- */
.bph-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.bph-btn .material-icons {
  font-size: 18px;
}

.bph-btn-sm {
  min-height: 34px;
  padding: 0 14px;
  font-size: 13px;
  margin-left: 8px;
}

.bph-btn-ghost {
  border-color: var(--bph-line-strong);
  color: var(--bph-ink);
}

.bph-btn-ghost:hover {
  background: var(--bph-bg-soft);
  border-color: #8d96a0;
}

.bph-btn-undo {
  background: var(--bph-brand);
  border-color: var(--bph-brand);
  color: #fff;
}

.bph-btn-cancel {
  background: #fff;
  border-color: var(--bph-down);
  color: var(--bph-down);
}

.bph-btn-undo:hover {
  background: #15619f;
}

/* ---------- detail modal ---------- */
.bph-dialog {
  max-width: min(1000px, 94vw);
  margin: 1.5rem auto;
}

.bph-modal {
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(16, 24, 32, 0.18);
}

.bph-modal-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--bph-line);
}

.bph-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.bph-modal-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--bph-ink-soft);
}

.bph-close {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--bph-ink-soft);
  cursor: pointer;
}

.bph-close:hover {
  background: var(--bph-bg-soft);
  color: var(--bph-ink);
}

.bph-close .material-icons {
  font-size: 20px;
}

.bph-modal-body {
  padding: 20px 24px;
}

.bph-modal-foot {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid var(--bph-line);
}

.bph-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  font-size: 12.5px;
  color: var(--bph-ink-soft);
}

.bph-note .material-icons {
  font-size: 18px;
}

@media (max-width: 640px) {
  .bph {
    padding: 16px;
  }

  .bph-head {
    flex-direction: column;
  }
}
</style>
